import { FormEvent } from 'react';
import { Link } from "react-router-dom";
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { trans } from "~/utils/trans";
import { setJWT } from "~/utils/utils";
import { login } from "~/providers/api";
import { userStore } from "~/stores/user";
import { notificationStore } from '~/stores/notification';
import { DEFAULT_USER, ROUTES } from "~/const";
import { myUseState } from "~/hooks/myUseState";

import "./style.less";

interface IState {
	email: string;
	emailError: boolean;
	password: string;
	passwordError: boolean;
	showPassword: boolean;
}

interface ILoginForm {
	// po uspesnem prihlaseni, napr. presmerovani
	onSuccess?: () => void;
}

// prihlasovaci formular, pouziva ho prihlasovaci stranka i stranky pro prihlasene (AccessGate)
export default function LoginForm({
	onSuccess = () => {},
} : ILoginForm) {
	const loginEvent = userStore(userState => userState.login);
	const setNotification = notificationStore.getState().setNotification;
	const { state, updateState } = myUseState<IState>({
		email: DEFAULT_USER.email,
		emailError: false,
		password: DEFAULT_USER.password,
		passwordError: false,
		showPassword: false,
	});

	async function onSubmit(event: FormEvent) {
		// odeslani i klavesou Enter, bez znovunacteni stranky
		event.preventDefault();

		const changes: Partial<IState> = {
			emailError: false,
			passwordError: false,
		};
		let errors = 0;

		if (!state.email) {
			changes.emailError = true;
			errors++;
		}

		if (!state.password) {
			changes.passwordError = true;
			errors++;
		}

		updateState({
			...changes,
		});

		if (errors > 0) {
			return;
		}

		const loginData = await login(state.email, state.password);

		// nastavime data a chyby
		if (loginData.noErrors) {
			// po loginu nastavime jwt
			setNotification(trans("notifications.login.success"));
			setJWT(loginData.jwt);
			loginEvent({
				...loginData.data,
				jwt: loginData.jwt,
			});
			onSuccess();
		} else {
			setNotification(loginData.errors.join(", "), "error");
		}
	}

	return <form className="loginForm" onSubmit={onSubmit} noValidate>
		<FormControl fullWidth sx={{ my: 1 }}>
			<TextField
				label={trans("common.email")}
				value={state.email}
				onChange={event => updateState({ email: event.target.value })}
				size="small"
				type="email"
				placeholder={trans("loginPage.email")}
				InputLabelProps={{ shrink: true }}
				InputProps={{
					autoComplete: "username",
					startAdornment: <InputAdornment position="start"><MailOutlineIcon fontSize="small" /></InputAdornment>,
				}}
				sx={{ background: "var(--theme-inputBg)" }}
				{ ...state.emailError
					? {
						error: true,
						helperText: trans("notifications.login.emptyEmail"),
					}
					: {}
				}
			/>
		</FormControl>
		<FormControl fullWidth sx={{ my: 1 }}>
			<TextField
				label={trans("registerPage.password")}
				value={state.password}
				onChange={event => updateState({ password: event.target.value })}
				size="small"
				InputLabelProps={{ shrink: true }}
				InputProps={{
					autoComplete: "current-password",
					startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>,
					endAdornment: <InputAdornment position="end">
						<IconButton
							aria-label={trans(state.showPassword ? "loginPage.hidePassword" : "loginPage.showPassword")}
							onClick={() => updateState({ showPassword: !state.showPassword })}
							edge="end"
							size="small"
						>
							{ state.showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" /> }
						</IconButton>
					</InputAdornment>,
				}}
				sx={{ background: "var(--theme-inputBg)" }}
				type={state.showPassword ? "text" : "password"}
				{ ...state.passwordError
					? {
						error: true,
						helperText: trans("notifications.login.emptyPassword"),
					}
					: {}
				}
			/>
		</FormControl>
		<div className="loginForm__forgot">
			<Link to={ROUTES.forgotPassword}>{trans("loginPage.forgotPassword")}</Link>
		</div>
		<div className="loginForm__buttons">
			<Button className="MyButton" type="submit" variant="contained" fullWidth size="large">{trans("loginPage.submit")}</Button>
		</div>
	</form>;
}
